import { Box } from "@mui/material";
import Enumerable from "linq";
import React, { useEffect, useMemo, useState, type JSX } from "react";
import { Components, GroupedVirtuoso } from "react-virtuoso";
import { VirtualizedListItem } from "../virtualized-list/VirtualizedList";

export type GroupedVirtualizedListItem<TGroupKeyType = number> = {
    groupKey: TGroupKeyType;
} & VirtualizedListItem;

export type GroupedVirtualizedListProps<TGroupedVirtualizedListItem, TGroupKeyType = number> = {
    items: Array<TGroupedVirtualizedListItem>;
    renderItem: (index: number, groupKey: TGroupKeyType, item: TGroupedVirtualizedListItem) => JSX.Element;
    renderGroup: (groupKey: TGroupKeyType) => JSX.Element;
    filter?: (item: TGroupedVirtualizedListItem) => boolean;
};

type GroupCountType<TGroupKeyType> = {
    key: TGroupKeyType;
    count: number;
};

export default function GroupedVirtualizedList<TGroupedVirtualizedListItem extends GroupedVirtualizedListItem<TGroupKeyType>, TGroupKeyType = number>({
    items,
    renderItem,
    renderGroup,
    filter,
}: GroupedVirtualizedListProps<TGroupedVirtualizedListItem, TGroupKeyType>) {
    const [filteredItems, setFilteredItems] = useState<Array<TGroupedVirtualizedListItem>>(new Array<TGroupedVirtualizedListItem>());
    const [groupCounts, setGroupCounts] = useState<Array<GroupCountType<TGroupKeyType>>>(new Array<GroupCountType<TGroupKeyType>>());

    useEffect(() => {

        function compareFn(a: TGroupedVirtualizedListItem, b: TGroupedVirtualizedListItem): -1 | 1 | 0 {

            if ((typeof a.groupKey === 'string')) {
                return (a.groupKey as string).localeCompare(b.groupKey as string) as (-1 | 1 | 0);
            }
            
            if (a.groupKey < b.groupKey) {
                return -1;
            }
            if (a.groupKey > b.groupKey) {
                return 1;
            }
            return 0;
        }

        const filteredItems = (typeof filter === "function"
            ? items.filter(item => filter(item))
            : items).sort(compareFn);

        setFilteredItems(filteredItems);

        const groupCounts = Enumerable
            .from(filteredItems)
            .groupBy(item => item.groupKey)
            .select(item => {
                return {
                    key: item.key(),
                    count: item.count()
                };
            })
            .toArray();

        setGroupCounts(groupCounts);

    }, [items, filter]);

    function internalRenderItem(index: number, groupIndex: number, _item: TGroupedVirtualizedListItem): JSX.Element {
        const item = filteredItems[index];
        return renderItem(index, groupCounts[groupIndex].key, item);
    }

    function internalRenderGroup(index: number): JSX.Element {
        const groupIndex = groupCounts[index].key;
        return renderGroup(groupIndex);
    }

    const Components = useMemo(() => {
        return {
            List: React.forwardRef(({ style, children }: any, listRef: any) => {
                return (
                    <Box
                        style={{ ...style }}
                        ref={listRef}
                    >
                        {children}
                    </Box>
                );
            })
        } as Components<TGroupedVirtualizedListItem, unknown>;
    }, []);

    return (
        <GroupedVirtuoso
            components={Components}
            itemContent={internalRenderItem}
            groupContent={internalRenderGroup}
            groupCounts={groupCounts.map(gc => gc.count)}
        />
    );
}
